// v1.0.0 - 2026-01-27 - GSE Excel file types

/**
 * GSE Excel file type
 */
export type GSEFileType = 'CORRISPETTIVI' | 'MISURE_ORARIE' | 'RIEPILOGO'

/**
 * Raw data from GSE Corrispettivi Excel
 */
export interface GSERawCorrispettivi {
  codice_contratto: string
  anno: number
  mese: number
  tip_eur: number
  cuafa_eur: number
  costi_gse_eur: number
  eaci_kwh: number
  totale_lordo_eur: number
}

/**
 * Raw data from GSE Riepilogo sheet
 */
export interface GSERawRiepilogo {
  codice_contratto: string
  pod: string
  tipo: 'IMMESSA' | 'PRELEVATA'
  energia_totale_kwh: number
  periodo: string // "202501" format
}

/**
 * Raw hourly data from GSE Curva sheet
 */
export interface GSERawCurvaOraria {
  pod: string
  data: string // "DD/MM/YYYY" format
  ora: number // 1-24 (GSE format) - will be converted to 0-23
  tipo: 'IMMESSA' | 'PRELEVATA'
  energia_kwh: number
}

/**
 * Parsed and normalized hourly data
 */
export interface ParsedMisuraOraria {
  pod: string
  date: string // "YYYY-MM-DD" ISO format
  hour: number // 0-23
  tipo: 'IMMESSA' | 'PRELEVATA'
  energia_kwh: number
}

/**
 * Aggregated energy data per member
 */
export interface AggregatedMemberEnergy {
  pod: string
  periodo: string // "2025-01" format
  immessa_totale_kwh: number
  prelevata_totale_kwh: number
  immessa_oraria: number[] // 24 values per day, flattened for the period
  prelevata_oraria: number[] // 24 values per day, flattened for the period
}

/**
 * Parser result with validation info
 */
export interface ParseResult<T> {
  success: boolean
  data: T | null
  errors: string[]
  warnings: string[]
  rowCount: number
  skippedRows: number
}

/**
 * Multiple files parse result
 */
export interface MultiFileParseResult {
  corrispettivi: ParseResult<GSERawCorrispettivi[]> | null
  misure: ParseResult<ParsedMisuraOraria[]> | null
  riepilogo: ParseResult<GSERawRiepilogo[]> | null
  aggregated: AggregatedMemberEnergy[] | null
}

/**
 * Expected column mappings for GSE files
 */
export const GSE_COLUMN_MAPPINGS = {
  CORRISPETTIVI: {
    codice_contratto: ['Codice Contratto', 'CODICE CONTRATTO', 'codice_contratto'],
    anno: ['Anno', 'ANNO', 'anno'],
    mese: ['Mese', 'MESE', 'mese'],
    tip: ['TIP', 'Tip', 'tip', 'Remunerazione'],
    cuafa: ['CUAfa', 'CUAFA', 'cuafa', 'Valorizzazione'],
    costi_gse: ['Costi GSE', 'COSTI GSE', 'costi_gse', 'Oneri Amministrativi'],
    eaci: ['EACI', 'Eaci', 'eaci', 'Energia Condivisa'],
  },
  RIEPILOGO: {
    pod: ['POD', 'Pod', 'pod', 'Punto di Connessione'],
    tipo: ['Tipo', 'TIPO', 'tipo', 'Tipologia'],
    energia: ['Energia', 'ENERGIA', 'energia', 'Totale kWh', 'Totale'],
    periodo: ['Periodo', 'PERIODO', 'periodo', 'Mese'],
  },
  CURVA: {
    pod: ['POD', 'Pod', 'pod'],
    data: ['Data', 'DATA', 'data', 'Giorno'],
    ora: ['Ora', 'ORA', 'ora', 'H'],
    energia: ['Energia', 'ENERGIA', 'energia', 'kWh', 'Valore'],
  },
} as const

/**
 * GSE file naming patterns
 */
export const GSE_FILE_PATTERNS = {
  CORRISPETTIVI: /corrispettivi|tip|incentiv/i,
  MISURE_ORARIE: /curva|orari|hour/i,
  RIEPILOGO: /riepilogo|summary|totali/i,
} as const
